import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEventHandler,
  type MouseEventHandler,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';

export interface RowMenuItem {
  label: string;
  onClick: () => void;
  destructive?: boolean;
  disabled?: boolean;
}

export interface RowMenuTriggerProps {
  ref: RefObject<HTMLButtonElement>;
  type: 'button';
  onClick: MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  'aria-expanded': boolean;
  'aria-haspopup': 'menu';
}

interface RowMenuProps {
  items: RowMenuItem[];
  label?: string;
  disabled?: boolean;
  renderTrigger?: (props: RowMenuTriggerProps) => ReactNode;
}

interface RowMenuItemsProps {
  items: RowMenuItem[];
  focusIndex?: number;
  registerItem?: (index: number, node: HTMLButtonElement | null) => void;
  onSelect: (item: RowMenuItem) => void;
}

/** Why the panel closed — decides whether focus returns to the trigger. */
export type RowMenuCloseReason = 'escape' | 'tab' | 'select' | 'trigger' | 'outside';

export type RowMenuFocusMove = 'first' | 'last' | 'next' | 'previous';

const POPUP_WIDTH = 180;
const VERTICAL_GAP = 6;
const DEFAULT_LABEL = 'More actions';

/**
 * Hidden until the row is hovered on desktop, always visible on touch.
 *
 * The reveal is spelled out at both scales. The bare `focus-visible:` utility
 * is emitted before the `md:` block, so above 768px it only beats `md:opacity-0`
 * on specificity — a keyboard-focused trigger stays visible by accident rather
 * than by intent, and any later `md:` opacity rule would silently hide it.
 * `md:focus-visible:` puts the reveal inside the same block it has to win in.
 */
const TRIGGER_CLASS =
  'rounded-input p-2 text-faint opacity-100 transition-all duration-200 hover:bg-elevated hover:text-fg focus-visible:opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100 disabled:cursor-not-allowed disabled:opacity-50';

export function nextRowMenuOpen(open: boolean, action: 'open' | 'close' | 'toggle'): boolean {
  if (action === 'toggle') return !open;
  return action === 'open';
}

export function shouldCloseRowMenuForKey(key: string): boolean {
  return key === 'Escape';
}

export function isOutsideRowMenuTarget(
  target: Node,
  trigger: Pick<Node, 'contains'> | null,
  popup: Pick<Node, 'contains'> | null,
): boolean {
  return !trigger?.contains(target) && !popup?.contains(target);
}

export function rowMenuFocusMoveForKey(key: string): RowMenuFocusMove | null {
  if (key === 'ArrowDown') return 'next';
  if (key === 'ArrowUp') return 'previous';
  if (key === 'Home') return 'first';
  if (key === 'End') return 'last';
  return null;
}

/**
 * Roving focus across enabled menu items only, wrapping at both ends.
 * Returns -1 when nothing is focusable.
 */
export function nextRowMenuFocusIndex(
  items: Pick<RowMenuItem, 'disabled'>[],
  current: number,
  move: RowMenuFocusMove,
): number {
  const enabled = items.reduce<number[]>((indexes, item, index) => {
    if (!item.disabled) indexes.push(index);
    return indexes;
  }, []);
  if (enabled.length === 0) return -1;
  if (move === 'first') return enabled[0];
  if (move === 'last') return enabled[enabled.length - 1];

  const position = enabled.indexOf(current);
  if (position === -1) return move === 'next' ? enabled[0] : enabled[enabled.length - 1];
  const step = move === 'next' ? 1 : -1;
  return enabled[(position + step + enabled.length) % enabled.length];
}

/**
 * Focus-return policy. Escape and Tab always hand focus back to the trigger;
 * a selection only does so while the trigger is still enabled (an action that
 * sets busy immediately would otherwise drop focus to the body); an outside
 * click leaves focus wherever the user just put it.
 */
export function shouldRestoreRowMenuFocus(
  reason: RowMenuCloseReason,
  triggerDisabled: boolean,
): boolean {
  if (reason === 'outside') return false;
  if (reason === 'escape' || reason === 'tab') return true;
  return !triggerDisabled;
}

export function RowMenuItems({ items, focusIndex, registerItem, onSelect }: RowMenuItemsProps) {
  return items.map((item, index) => {
    const beginsDestructiveGroup = Boolean(
      item.destructive
      && index > 0
      && !items[index - 1]?.destructive
      && items.slice(0, index).some((candidate) => !candidate.destructive),
    );
    return (
      <div key={`${item.label}-${index}`} role="none">
        {beginsDestructiveGroup ? <div role="separator" className="my-1 border-t border-line" /> : null}
        <button
          ref={registerItem ? (node) => registerItem(index, node) : undefined}
          type="button"
          role="menuitem"
          tabIndex={-1}
          data-focused={focusIndex === index ? 'true' : undefined}
          className={`w-full px-3 py-2 text-left text-[13px] font-medium transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-50 ${item.destructive ? 'text-error hover:bg-error-tint' : 'text-fg hover:bg-elevated'}`}
          disabled={item.disabled}
          onClick={() => onSelect(item)}
        >
          {item.label}
        </button>
      </div>
    );
  });
}

/**
 * Portalled overflow menu for row actions and compact custom-trigger menus.
 * The fixed-position panel escapes row stacking contexts and tracks its trigger.
 */
export default function RowMenu({ items, label = DEFAULT_LABEL, disabled, renderTrigger }: RowMenuProps) {
  const [open, setOpen] = useState(false);
  const [popupStyle, setPopupStyle] = useState<CSSProperties | null>(null);
  const [focusIndex, setFocusIndex] = useState(-1);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const positionPopup = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    setPopupStyle({
      position: 'fixed',
      top: rect.bottom + VERTICAL_GAP,
      right: window.innerWidth - rect.right,
      width: POPUP_WIDTH,
      zIndex: 60,
    });
  }, []);

  const restoreFocus = useCallback((reason: RowMenuCloseReason) => {
    const trigger = buttonRef.current;
    if (!trigger) return;
    if (!shouldRestoreRowMenuFocus(reason, trigger.disabled)) return;
    trigger.focus();
  }, []);

  const closeMenu = useCallback((reason: RowMenuCloseReason) => {
    setOpen((current) => nextRowMenuOpen(current, 'close'));
    setFocusIndex(-1);
    // Tab has to land on the trigger before the browser's own tab step runs, so
    // focus moves on from there naturally. Every other close waits a frame for
    // the panel to unmount and for any busy state the action set to settle.
    if (reason === 'tab') {
      restoreFocus(reason);
      return;
    }
    window.requestAnimationFrame(() => restoreFocus(reason));
  }, [restoreFocus]);

  const toggle: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (open) {
      closeMenu('trigger');
      return;
    }
    positionPopup();
    itemRefs.current = [];
    setFocusIndex(nextRowMenuFocusIndex(items, -1, 'first'));
    setOpen((current) => nextRowMenuOpen(current, 'toggle'));
  };

  const onPopupKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (event.key === 'Tab') {
      closeMenu('tab');
      return;
    }
    const move = rowMenuFocusMoveForKey(event.key);
    if (move === null) return;
    event.preventDefault();
    setFocusIndex((current) => nextRowMenuFocusIndex(items, current, move));
  };

  useEffect(() => {
    if (!open || focusIndex < 0) return;
    itemRefs.current[focusIndex]?.focus();
  }, [focusIndex, open]);

  useEffect(() => {
    if (!open) return;

    function onDocumentMouseDown(event: MouseEvent) {
      const target = event.target;
      if (target instanceof Node && isOutsideRowMenuTarget(target, buttonRef.current, popupRef.current)) {
        closeMenu('outside');
      }
    }

    function onDocumentKeyDown(event: KeyboardEvent) {
      if (shouldCloseRowMenuForKey(event.key)) closeMenu('escape');
    }

    document.addEventListener('mousedown', onDocumentMouseDown);
    document.addEventListener('keydown', onDocumentKeyDown);
    window.addEventListener('scroll', positionPopup, true);
    window.addEventListener('resize', positionPopup);
    return () => {
      document.removeEventListener('mousedown', onDocumentMouseDown);
      document.removeEventListener('keydown', onDocumentKeyDown);
      window.removeEventListener('scroll', positionPopup, true);
      window.removeEventListener('resize', positionPopup);
    };
  }, [closeMenu, open, positionPopup]);

  const triggerProps: RowMenuTriggerProps = {
    ref: buttonRef,
    type: 'button',
    onClick: toggle,
    disabled,
    'aria-expanded': open,
    'aria-haspopup': 'menu',
  };

  return (
    <>
      {renderTrigger ? renderTrigger(triggerProps) : (
        <button
          {...triggerProps}
          aria-label={label}
          title={label}
          className={TRIGGER_CLASS}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <circle cx="5" cy="12" r="1.6" />
            <circle cx="12" cy="12" r="1.6" />
            <circle cx="19" cy="12" r="1.6" />
          </svg>
        </button>
      )}

      {open && popupStyle && createPortal(
        <div
          ref={popupRef}
          role="menu"
          aria-label={label}
          style={popupStyle}
          className="min-w-[180px] rounded-input border border-line bg-card py-1 shadow-floating"
          onKeyDown={onPopupKeyDown}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          <RowMenuItems
            items={items}
            focusIndex={focusIndex}
            registerItem={(index, node) => {
              itemRefs.current[index] = node;
            }}
            onSelect={(item) => {
              closeMenu('select');
              item.onClick();
            }}
          />
        </div>,
        document.body,
      )}
    </>
  );
}
